/**
 * The per-trade chart. Backlog task 71.
 *
 * `UI-Design-Spec.md`: *"Clicking a trade opens the chart at that moment with the strategy's own
 * indicators drawn — the single most useful debugging view in the whole product."*
 *
 * ## The indicators are not drawn, and that is not laziness
 *
 * The API stores no per-bar indicator series, so drawing them means recomputing `rsi` or `sma`
 * in the browser. `Strategy-SDK.md` forbids exactly that: *"one canonical implementation per
 * indicator, shared by strategies, the backtester, and the chart view. A chart drawing a
 * different RSI from the strategy is a trap."* A TypeScript RSI beside a `pandas-ta` one is two
 * implementations with no test that they agree, on the screen whose whole job is to explain why
 * a trade happened.
 *
 * So this draws what the stored data supports honestly: **the real price bars around the trade,
 * with the entry and exit marked**. That is most of the value — where in the range the fill
 * landed, how far it went against before it went for, whether the exit was a stop or a signal —
 * and none of it is invented.
 *
 * The gap is real and belongs in the backlog rather than in a comment: `ctx.plot()` exists in
 * the SDK for a strategy to emit its own series, and nothing persists what it emits. That is the
 * mechanism the vault's sentence actually needs.
 */

import { useQuery } from '@tanstack/react-query';

import { api } from '../api/client';
import type { TradeResponse } from '../api/generated';
import { Timeframe } from '../api/generated';
import { MODE_LABELS, useTimestampMode } from '../ui/timestamps';
import { CandleChart } from './CandleChart';
import { ChartFrame } from '../design/components/data/ChartFrame.jsx';
import { EmptyState } from '../design/components/messages/EmptyState.jsx';
import { Skeleton } from '../design/components/messages/Skeleton.jsx';
import { formatDecimal, formatPrice } from '../money';

/** Seconds per timeframe, mirroring `jmt_domain.Timeframe`. */
const INTERVAL_SECONDS: Record<string, number> = {
  '1m': 60,
  '5m': 300,
  '15m': 900,
  '1h': 3600,
  '4h': 14400,
  '1d': 86400,
};

/**
 * Bars of context on each side of the trade.
 *
 * Enough to see what the price was doing before the entry — a trade shown with no run-up is a
 * fill with no explanation — and bounded, because this is a debugging view opened by a click and
 * not a chart somebody navigates.
 */
const CONTEXT_BARS = 40;

export function TradeChart({
  trade,
  timeframe,
  theme = 'dark',
}: {
  trade: TradeResponse;
  timeframe: string;
  theme?: 'dark' | 'light';
}): React.JSX.Element {
  const timestampMode = useTimestampMode();
  const interval = INTERVAL_SECONDS[timeframe] ?? 3600;
  const entry = new Date(trade.entry_ts).getTime();
  const exit = trade.exit_ts === null ? entry : new Date(trade.exit_ts).getTime();
  const from = new Date(entry - CONTEXT_BARS * interval * 1000).toISOString();
  const to = new Date(exit + CONTEXT_BARS * interval * 1000).toISOString();

  const bars = useQuery({
    queryKey: ['trade-bars', trade.id, timeframe],
    queryFn: () =>
      api.marketData.listBars({
        instrumentId: trade.instrument_id,
        timeframe: timeframe as Timeframe,
        from,
        to,
        limit: 500,
      }),
  });

  const held =
    trade.exit_ts === null
      ? '—'
      : `${Math.round((exit - entry) / (interval * 1000))} bars`;

  return (
    <ChartFrame
      title={`Trade #${trade.id} · ${trade.side} · held ${held}`}
      // The caption follows the axis, which follows the header. Three places, one answer.
      timezone={MODE_LABELS[timestampMode]}
      // No range selector. `ChartFrame` offers 1d/7d/30d/1y/all by default and every one of
      // them is inert here: the window is the trade's, chosen by the trade. An inert control
      // is worse than none — it invites a click that does nothing.
      ranges={[]}
      legend={[
        { label: `entry ${formatPrice(trade.entry_price)}` },
        {
          label: `exit ${trade.exit_price === null ? '—' : formatPrice(trade.exit_price)}`,
        },
        {
          label: `net ${formatDecimal(trade.net_pnl ?? '0', 2)}`,
          colour: Number(trade.net_pnl ?? 0) < 0 ? 'var(--loss)' : 'var(--profit)',
        },
      ]}
    >
      {bars.isLoading ? (
        <Skeleton height={280} />
      ) : bars.isError || (bars.data?.bars ?? []).length === 0 ? (
        <EmptyState>
          No bars stored for this window. The trade is recorded; the price history around it is
          not, which happens when a backtest ran over a range that has since been pruned.
        </EmptyState>
      ) : (
        <CandleChart
          bars={bars.data!.bars}
          intervalSeconds={interval}
          height={280}
          theme={theme}
        />
      )}
      <p className="jmt-note-text">
        The strategy&rsquo;s own indicators are <strong>not</strong> drawn.{' '}
        <code>Strategy-SDK.md</code> allows one implementation of each indicator, shared by the
        strategy, the backtester and the chart — <em>&ldquo;a chart drawing a different RSI from
        the strategy is a trap&rdquo;</em> — and nothing persists what <code>ctx.plot()</code>{' '}
        emits, so there is no series here that came from the strategy itself.
      </p>
    </ChartFrame>
  );
}
